import { Component, Element, Event, EventEmitter, h, Method, Prop } from '@stencil/core';
import Bind from 'bind-decorator';
import React from 'react';
import { whenActuallyActivated } from '../../../utils/accessibility';
import { generateCssClasses } from '../../../utils/generate-css-classes';
import { hasSlotted } from '../../../utils/slotted';
import { NavigationMaybeKeyboardEventDetail } from '../zds-navigation';

@Component({
	shadow: true,
	styleUrl: 'zds-navigation-link.scss',
	tag: 'zds-navigation-link',
})
export class ZdsNavigationLink {
	@Element() hostElement: HTMLDivElement;

	@Event({ bubbles: true }) zdsNavigationActivateBody: EventEmitter<NavigationMaybeKeyboardEventDetail>;
	@Event({ bubbles: true }) zdsClick: EventEmitter<MouseEvent | KeyboardEvent>;

	@Prop({ mutable: true, reflect: true }) active: boolean;
	@Prop() link: string;
	@Prop() linkTarget: string;

	@Method()
	async activate(isKeyboardActivated: boolean = false) {
		if (this.link) return;

		// Only activate body if slotted in one
		const parentElement = this.hostElement.parentElement;
		if (
			parentElement.localName !== 'zds-navigation-body' ||
			this.hostElement.getAttribute('slot') !== 'navigation-link'
		) {
			return;
		}

		const identifier = (parentElement as HTMLZdsNavigationBodyElement).identifier;

		this.active = !this.active;
		this.zdsNavigationActivateBody.emit({
			identifier,
			isKeyboardActivated,
		});
	}

	@Bind
	async onPotentialActivate(event: MouseEvent | KeyboardEvent) {
		whenActuallyActivated(
			event,
			() => {
				this.zdsClick.emit(event);
				void this.activate(event.type === 'keydown');
			},
			{
				preventDefault: !this.link,
			},
		);
	}

	render() {
		let ComponentTag = 'div';
		let attributes = {};

		const linkClasses = generateCssClasses('zds-navigation-link', {
			active: this.active,
		});

		if (this.link) {
			ComponentTag = 'a';
			attributes = {
				href: this.link,
				target: this.linkTarget,
			};
		}

		return (
			<ComponentTag
				class={linkClasses}
				onClick={this.onPotentialActivate}
				onKeyDown={this.onPotentialActivate}
				tabindex="0"
				{...attributes}
			>
				{this.renderIcon('start')}
				<span class="zds-navigation-link__label">
					<slot />
				</span>
				{this.renderIcon('end')}
			</ComponentTag>
		);
	}

	renderIcon(position: string) {
		const slotName = `icon-${position}`;

		if (!hasSlotted(this.hostElement, slotName)) {
			return null;
		}

		// Determine whether this is a chevron or a custom icon
		const iconSlot =
			this.hostElement.querySelector<HTMLZdsIconElement>(`:scope > zds-icon[slot="${slotName}"]`) || null;
		const iconName = iconSlot?.name;
		const iconType = iconName === 'chevron-down' ? 'chevron' : 'custom';

		const iconClasses = generateCssClasses('zds-navigation-link__icon', {
			[position]: true,
			[`type-${iconType}`]: true,
		});

		let iconContent: React.JSX.Element;
		if (iconType === 'custom' && this.active) {
			iconContent = <zds-icon name="xmark" library="fa-solid" size={4}></zds-icon>;
		} else {
			iconContent = <slot name={slotName}></slot>;
		}

		return <span class={iconClasses}>{iconContent}</span>;
	}
}
